<script lang="ts">
    import Vue from 'vue';

    export default Vue.extend({
        head: {
			title: { inner: 'Tennis Membership Fee Collection for your Club', separator: '|', complement: 'Str8 Sets' },
            meta: [
				{ name: 'description', content: 'Want to simplify membership fee collection? Your members can now renew with a single click or set it to auto-renew for a seamless experience. No matter the business models, billing cycles and tiering of membership types, Str8 Sets for Clubs gives you confidence when it comes to membership fee collection.', id: 'desc'},
                { property: 'og:type', content: 'website' },
				{ property: 'og:url', content: 'https://www.str8sets.com/tennis_club_member_fee_collection' },
                { property: 'og:title', content: 'Tennis Membership Fee Collection for your Club | Str8 Sets' },
                { property: 'og:description', content: 'Want to simplify membership fee collection? Your members can now renew with a single click or set it to auto-renew for a seamless experience. No matter the business models, billing cycles and tiering of membership types, Str8 Sets for Clubs gives you confidence when it comes to membership fee collection.' },
                { property: 'og:image', content: 'https://www.str8sets.com/img/meta_str8sets_tennis_eco_1200x628.jpg' },   
				{ property: 'og:image:alt', content: 'Str8 Sets Tennis Logo and Screenshot' },            
                { name: 'twitter:site', content: 'str8sets' },
				{ name: 'twitter:creator', content: '@str8sets' },
				{ name: 'twitter:card', content: 'summary_large_image' },  
				{ name: 'twitter:url', content: 'https://www.str8sets.com/tennis_club_member_fee_collection' },
				{ name: 'twitter:title', content: 'Tennis Membership Fee Collection for your Club | Str8 Sets' },
                { name: 'twitter:description', content: 'Want to simplify membership fee collection? Your members can now renew with a single click or set it to auto-renew for a seamless experience. No matter the business models, billing cycles and tiering of membership types, Str8 Sets for Clubs gives you confidence when it comes to membership fee collection.' },
				{ name: 'twitter:image', content: 'https://www.str8sets.com/img/meta_str8sets_tennis_eco_1200x628.jpg' },
				{ name: 'twitter:image:alt', content: 'Str8 Sets Tennis Logo and Screenshot' }
            ],
            link: [
      			{ rel: 'canonical', href: 'https://www.str8sets.com/tennis_club_member_fee_collection', id: 'canonical' }
			]
        }
    })
</script>

<template>

    <div>
        <div class="parallax image-two-players-above height-350">
            <div class="container">
                <div class="feature-page-header">
                    <h1>Membership Fee Collection</h1>
                    <h2>Effortless membership repayment</h2>
                </div>
                <div class="row">
                    <router-link class="clear_button" to="/case-studies_clubs" role="button">VIEW CLUB CASE STUDIES</router-link>
                    <router-link class="clear_button" to="/faq#membership" role="button">FREQUENT QUESTIONS</router-link>
                </div>
            </div>
        </div>   
        
        <section class="feature-list section">
            <div class="container">
                <div class="row">
                    <div class="col-md-12">
                        <div class="content mt-100">
                            <p>We believe that <strong>efficient club management will lead to a better club</strong>, a happier membership and better tennis for all. Membership is the backbone of a 
                                tennis club and therefore needs efficient management with minimal effort by the club manager who's always busy. That's why we included membership 
                                fee collection in our Str8 Sets product suite for clubs.</p>
                                <p>So stop handling membership payments manually or through various vendors and <strong>use Str8 Sets to manage all your tennis club's needs</strong>.</p>
                        </div>
                    </div>
                </div>
                <div class="spacer-large"></div>
                <div class="row">
                    <div class="col-md-12 heading">
                        <img class="icon" src="@/assets/images/payments.svg" alt="Green Payments Icon" data-no-retina>
                        <h2>How does Membership Fee Collection work?</h2>
                        <h4>One-click member renewals</h4>
                    </div>                            
                </div>
                <div class="row">                               
                    <div class="col-md-12">
                        <div class="content mt-100">
                            <p>This feature enables great flexibility in terms of how membership fees are paid and collected. It allows for different business models, billing cycles 
                                and the usual tiering of membership types. You set the prices of each membership type and the rules about when members pay.</p>
                            <p>Some clubs renew all members at the same time and others let each member have their own expiry date. Some clubs require direct debits and others let 
                                the member decide. With Str8 Sets you get all that.</p>
                        </div>
                    </div>
                </div>
                <div class="spacer-large"></div>
                <div class="container">
                    <div class="row">
                        <div class="col-md-6 text-center"> <img class="img_monitor zoom-hover" :src="'/img/dashboard-club-members.png'" alt="Str8 Sets Dashboard Screenshot (Club Membership List)" v-rjs="2"> </div>
                        <div class="col-md-6 text-center"> <img class="img_monitor zoom-hover" :src="'/img/dashboard-payments.png'" alt="Str8 Sets Dashboard Screenshot (Payments)" v-rjs="2"> </div>
                    </div>
                </div>
                <div class="spacer-large"></div>
                <div class="row">                               
                    <div class="col-md-12">
                        <div class="content mt-100">
                            <div class="text-heading">
                                <h2>Members renew with one-click</h2>
                            </div> 
                            <p>Once you setup your rules and have assigned a membership type and expiry date to each member, you can enable this feature and watch the membership start coming in. 
                                Your members will pay their fees with a single click on the Str8 Sets app. If you require automated recurring payments (direct debits) then they only need to 
                                make the initial payment and we do the rest every billing cycle. The membership fees are transferred by us to your Stripe account and from there to 
                                your bank account.</p>
                            <p>Your members will be notified when you enable this feature and will be reminded when their payment is due. Members will also automatically receive receipts for 
                                their payments and when their credit card nears the expiry date, we’ll remind them to add the new number. So now, wasting time, chasing membership payments is a 
                                thing of the past. Forget about manual membership fee reconciliation and have more time for the rest of your activities at the club! </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <div class="parallax image-two-players-above height-150" ></div>

        <section class="feature-list section">
            <div class="container">
                <div class="row">
                    <div class="col-md-12">
                        <div class="content mt-100">
                            <div class="text-heading">
                                <h2>Security</h2>
                            </div> 
                            <p>We fully understand the security requirements for dealing with financial transactions and that's why 
                            we adopt the state-of-the-art technology and security when handling payments. <b>Our systems are PCI 
                            compliant</b> and <u>we don't store any credit card information</u> on our servers. If you have any 
                            questions or comments please <router-link to="feedback">let us know</router-link> and we'll happily discuss 
                            them with you.</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>